import { FormEvent, ReactElement, useContext, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';

import { GtmEventType } from '@gn-audio/jss-core';

import { SiteFeature } from '../../../types';
import {
  getPostLoginActions,
  isAuthorized,
  savePostLoginActions,
  TrackingContext,
  useFallbackTranslation,
  useSiteSettings,
} from '../../../utils';
import { Button } from '../../Button/Button';
import { FeatureToggle } from '../../FeatureToggle';
import { useBasket } from '../BasketContext';
import Location from '../ShippingOutside/Location';
import { createEcommercePayload } from '../utilities/basketHelpers';
import BasketProducts from './BasketProducts';
import { CouponCodes } from './CouponCodes';
import ShippingMessage from './ShippingMessage';

import styles from '../Basket.module.css';

type CheckoutProps = {
  token: string;
  className: string;
};

const Checkout = (props: CheckoutProps): ReactElement | null => {
  const { token, className } = props;
  const { basket, openMyAccountView, toggleBasket, groups } = useBasket();
  const { sfcc } = useSiteSettings();
  const formRef = useRef<HTMLFormElement>(null);
  const { gtmEvent } = useContext(TrackingContext);

  const eCommercePayload = useMemo(
    () =>
      createEcommercePayload({
        eventType: GtmEventType.BeginCheckout,
        basket,
        groups,
        currency: sfcc?.currency,
      }),
    [basket, groups, sfcc?.currency]
  );
  const { t } = useFallbackTranslation();

  const checkIfLoggedIn = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    gtmEvent(eCommercePayload);

    if (isAuthorized()) {
      event.currentTarget.submit();
    } else {
      openMyAccountView();
    }
  };

  useEffect(() => {
    // TODO: temporarily disabled due to an issue with session synchronization (https://dev.azure.com/gn-global-it/Digital%20Experience%20GNA/_workitems/edit/688701)
    // TODO: waiting for a long-term solution (US 710769 - https://dev.azure.com/gn-global-it/Digital%20Experience%20GNA/_workitems/edit/710769)
    // const goToCheckout = async () => {
    //   if (isAuthorized() && formRef.current) {
    //     toggleBasket();
    //     formRef.current?.submit();
    //   }
    // };

    const [postLoginAction, ...otherActions] = getPostLoginActions();

    if (postLoginAction?.type === 'checkout') {
      savePostLoginActions(otherActions);
      if (!basket?.product_items?.length) {
        return;
      }
      toggleBasket();
    }
  }, [toggleBasket, basket, formRef]);

  const {
    order_price_adjustments = [],
    c_product_sub_total_formatted = '',
    c_product_before_discount_formatted = '',
    c_product_total_formatted = '',
  } = basket ?? {};

  const hasPriceAdjustments = order_price_adjustments.length > 0;

  const regularTotalPrice = hasPriceAdjustments
    ? c_product_sub_total_formatted
    : c_product_before_discount_formatted;

  const showRegularPrice = regularTotalPrice !== c_product_total_formatted;

  return (
    <div className={classNames('flex h-full flex-1 flex-col overflow-auto p-6', className)}>
      <div className="space-between flex flex-1 flex-col">
        <FeatureToggle featureName={SiteFeature.SfccBasketLocationWarning}>
          <Location />
        </FeatureToggle>
        <FeatureToggle featureName={SiteFeature.SfccBasketShippingMessage}>
          <ShippingMessage />
        </FeatureToggle>
        <BasketProducts />
      </div>
      <div className={styles.promoOrReference}>
        <CouponCodes />
      </div>
      <div className={classNames(styles.bottomSection, styles.fat)}>
        <div className="flex w-full flex-col">
          {hasPriceAdjustments && (
            <p className="space-between mb-4 flex flex-1 flex-row border-b border-gray-50 pb-4">
              <span className="flex flex-1 uppercase">{t('cart.OrderDiscount')}</span>
              <span>{basket?.c_order_discount_formatted}</span>
            </p>
          )}
          <p className="space-between flex flex-1 flex-row">
            <span className="flex flex-1 uppercase">{t('cart.SubTotal')}</span>
            <span className="flex gap-1">
              {showRegularPrice && (
                <span className="font-normal line-through">{regularTotalPrice}</span>
              )}
              <span>{basket?.c_product_total_formatted}</span>
            </span>
          </p>
        </div>
        <form
          ref={formRef}
          className="w-full"
          onSubmit={checkIfLoggedIn}
          method="POST"
          action={sfcc?.basketCheckoutUrl}
        >
          <input type="hidden" name="token" value={token} />
          <input type="hidden" name="action" value="Checkout-Begin" />
          <Button variant="primary" size="l" className="ml-3" type="submit">
            {t('cart.Checkout')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
